import { db } from "../firebaseConfig";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  deleteDoc
} from "firebase/firestore";

export default {
  name: "KnowledgeArticleEditorLogic",
  props: {
    // articleId passed from the route as /knowledge-editor/{articleId}
    initialArticleId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      // Use a local variable to hold and update the article ID
      localArticleId: null,
      
      // Article fields
      title: "",
      category: "",
      createdBy: "",
      // description will store the raw EditorJS JSON string
      description: "",
      timestamp: null,
      lastUpdatedTimestamp: null,
      
      error: null,
      loading: false,
      successMessage: "",
      
      // For listing all articles if needed (optional)
      articles: []
    };
  },
  async created() {
    // Use the passed prop or fall back to the route parameter (note the key 'articleId' must match your router)
    this.localArticleId = this.initialArticleId || (this.$route && this.$route.params.articleId);
    
    // If an article ID was provided, load that article
    if (this.localArticleId) {
      await this.fetchArticle(this.localArticleId);
    }
    
    // Optionally, fetch the list of articles (if you want a table below the editor)
    await this.fetchArticles();
  },
  methods: {
    // Fetch a single article by its ID and load its data
    async fetchArticle(articleId) {
      this.loading = true;
      try {
        const articleRef = doc(db, "knowledgeBlog", articleId);
        const docSnap = await getDoc(articleRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          this.title = data.title || "";
          this.category = data.category || "";
          this.createdBy = data.createdBy || "";
          this.description = data.description || "";
          this.timestamp = data.timestamp;
          this.lastUpdatedTimestamp = data.lastUpdatedTimestamp;
        } else {
          this.error = "Article not found.";
        }
      } catch (err) {
        console.error("Error fetching article:", err);
        this.error = "Error fetching article.";
      } finally {
        this.loading = false;
      }
    },
    
    // Save (create or update) an article
    async saveArticle() {
      this.loading = true;
      try {
        // Example: retrieve current user from your global state or auth
        const user = this.$root.$data.currentUser;
        const createdByName = user && user.displayName ? user.displayName : "Anonymous";
        
        // Ensure description is never undefined – fallback to an empty EditorJS JSON structure if needed
        const safeDescription = this.description || JSON.stringify({ blocks: [] });
        
        if (this.localArticleId) {
          // Update existing article
          const articleRef = doc(db, "knowledgeBlog", this.localArticleId);
          await updateDoc(articleRef, {
            title: this.title,
            category: this.category,
            description: safeDescription,
            lastUpdatedTimestamp: serverTimestamp()
          });
          this.successMessage = "Article updated!";
        } else {
          // Create new article
          const articlesRef = collection(db, "knowledgeBlog");
          const docRef = await addDoc(articlesRef, {
            title: this.title,
            category: this.category,
            createdBy: createdByName,
            description: safeDescription,
            timestamp: serverTimestamp(),
            lastUpdatedTimestamp: serverTimestamp()
          });
          this.localArticleId = docRef.id;
          this.successMessage = "Article created!";
        }
      } catch (err) {
        console.error("Error saving article:", err);
        this.error = "Error saving article.";
      } finally {
        this.loading = false;
        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      }
    },
    
    // (Optional) Fetch all articles for a list/table if needed
    async fetchArticles() {
      try {
        const articlesRef = collection(db, "knowledgeBlog");
        const snapshot = await getDocs(articlesRef);
        const articlesList = [];
        snapshot.forEach(docSnap => {
          articlesList.push({
            id: docSnap.id,
            ...docSnap.data()
          });
        });
        this.articles = articlesList;
      } catch (err) {
        console.error("Error fetching articles:", err);
        this.error = "Error fetching articles.";
      }
    },
    
    // (Optional) Delete an article from Firestore
    async deleteArticle(article) {
      const confirmDelete = window.confirm("Are you sure you want to delete this article?");
      if (!confirmDelete) return;
      try {
        const articleRef = doc(db, "knowledgeBlog", article.id);
        await deleteDoc(articleRef);
        this.articles = this.articles.filter(a => a.id !== article.id);
        this.successMessage = "Article deleted.";
        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      } catch (err) {
        console.error("Error deleting article:", err);
        this.error = "Failed to delete article.";
      }
    },
    
    // Load an article's data into our fields for editing
    editArticle(article) {
      this.localArticleId = article.id;
      this.title = article.title || "";
      this.category = article.category || "";
      this.createdBy = article.createdBy || "";
      this.description = article.description || "";
      this.timestamp = article.timestamp;
      this.lastUpdatedTimestamp = article.lastUpdatedTimestamp;
    }
  }
};
